import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Warning, FilmSlate, MagnifyingGlass } from "@phosphor-icons/react";

const COV = {
  FULL_COVERAGE: { label: "Full", cls: "bg-[#84CC16]/15 text-[#4D7C0F]" },
  PARTIAL_COVERAGE: { label: "Partial", cls: "bg-amber-100 text-amber-700" },
  POOR_COVERAGE: { label: "Poor", cls: "bg-orange-100 text-orange-700" },
  UNCOVERED: { label: "Uncovered", cls: "bg-red-100 text-red-700" },
};

// Period presets for comparing insights at a glance. Weeks start Monday, local time.
const periodRange = (key) => {
  const now = new Date();
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
  switch (key) {
    case "this_week": return [monday(day), null];
    case "last_week": { const m = monday(day); const s = new Date(m); s.setDate(s.getDate() - 7); return [s, m]; }
    case "this_month": return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case "last_month": return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    default: return [null, null];
  }
};
const PERIODS = [
  { key: "all", label: "All time" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
];

export default function CoachInsightsPanel() {
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(null);
  const [search, setSearch] = useState(null);
  const [tsBusy, setTsBusy] = useState(false);
  const [period, setPeriod] = useState("all");

  const loadTs = () => api.get("/admin/coach/transcribe-status").then(({ data: d }) => setTs(d)).catch(() => {});
  const loadInsights = (p) => {
    const [since, until] = periodRange(p);
    const params = {};
    if (since) params.since = since.toISOString();
    if (until) params.until = until.toISOString();
    api.get("/admin/coach/insights", { params }).then(({ data: d }) => setData(d))
      .catch(() => setData({ items: [], coverage_counts: {}, top_categories: [] }));
  };
  useEffect(() => {
    loadInsights("all");
    api.get("/admin/coach/search-analytics").then(({ data: d }) => setSearch(d)).catch(() => setSearch({ topics: [], total_searches: 0 }));
    loadTs();
  }, []);
  useEffect(() => {
    if (!ts?.state?.running) return;
    const t = setInterval(loadTs, 10000);
    return () => clearInterval(t);
  }, [ts?.state?.running]);

  const startTranscribe = async () => {
    setTsBusy(true);
    try {
      await api.post("/admin/coach/transcribe-videos");
      toast.success("Transcription started — this runs in the background");
      setTimeout(loadTs, 1500);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTsBusy(false); }
  };
  if (!data) return <div className="text-sm text-[#71717A] p-4">Loading…</div>;

  return (
    <div data-testid="coach-insights-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={20} weight="duotone" color="#84CC16" />
        <h2 className="font-display font-bold text-lg m-0">Coach Insights</h2>
      </div>
      <p className="text-xs text-[#52525B] mt-0 mb-4">
        Anonymized summaries of Coach conversations: what people need help with, which lessons answered it,
        and where the curriculum has gaps. Raw chats are deleted after summarization
        {data.pending_sessions ? ` (${data.pending_sessions} session${data.pending_sessions > 1 ? "s" : ""} awaiting summary — runs every 30 min).` : "."}
      </p>

      <div className="rounded-xl border border-[#C4C8CE] bg-white p-3 mb-4" data-testid="coach-transcribe-card">
        <div className="flex flex-wrap items-center gap-3">
          <FilmSlate size={18} weight="duotone" color="#84CC16" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs font-bold">Lesson video transcripts</div>
            <div className="text-[11px] text-[#52525B]">
              {ts ? `${ts.transcribed} of ${ts.lessons_with_video} video lessons transcribed.` : "Loading…"}{" "}
              Transcripts show under each video for students, and Coach uses them to cite what's actually said.
              {ts?.state?.running && ` Running: ${ts.state.done}/${ts.state.total}…`}
            </div>
            {(ts?.state?.errors?.length || 0) > 0 && (
              <div className="text-[11px] text-red-600 mt-1">Issues: {ts.state.errors.slice(-3).join(" · ")}</div>
            )}
          </div>
          <button onClick={startTranscribe} disabled={tsBusy || ts?.state?.running} data-testid="coach-transcribe-btn"
            className="btn-primary text-xs px-4 py-2 disabled:opacity-50">
            {ts?.state?.running ? "Transcribing…" : "Transcribe videos"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="insights-period-filter">
        {PERIODS.map((p) => (
          <button key={p.key} data-testid={`insights-period-${p.key}`}
            onClick={() => { setPeriod(p.key); loadInsights(p.key); }}
            className="text-xs font-bold px-3 py-1.5 rounded-full border cursor-pointer"
            style={period === p.key
              ? { background: "#0A0D14", color: "#CCFF00", borderColor: "#0A0D14" }
              : { background: "#fff", color: "#3F3F46", borderColor: "#C4C8CE" }}>
            {p.label}
          </button>
        ))}
        <span className="text-[11px] text-[#71717A] ml-1" data-testid="insights-period-count">
          {data.items.length} conversation{data.items.length === 1 ? "" : "s"}
          {period !== "all" ? " in this period" : ""}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(COV).map(([k, v]) => (
          <div key={k} className={`rounded-lg px-3 py-2 text-xs font-bold ${v.cls}`} data-testid={`coach-cov-${k}`}>
            {v.label} coverage: {data.coverage_counts?.[k] || 0}
          </div>
        ))}
        <div className="rounded-lg px-3 py-2 text-xs font-bold bg-[#0A0D14] text-[#CCFF00]" data-testid="coach-redirected-stat"
          title="Chats where Coach deliberately steered the user to a better or safer approach than what they literally asked for">
          Redirected to sounder advice: {data.redirected_count || 0}{data.items.length ? ` of ${data.items.length}` : ""}
        </div>
      </div>

      {data.top_categories?.length > 0 && (
        <div className="rounded-xl border border-[#C4C8CE] bg-white p-3 mb-4">
          <div className="text-xs font-bold mb-1.5">What people ask about most</div>
          <div className="flex flex-wrap gap-1.5">
            {data.top_categories.map((c) => (
              <span key={c.category} className="text-[11px] rounded-full bg-[#F4F4F5] border border-[#E4E4E7] px-2.5 py-1">
                {c.category} · {c.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {search?.topics?.length > 0 && (
        <div className="rounded-xl border border-[#C4C8CE] bg-white p-3 mb-4" data-testid="search-analytics-card">
          <div className="flex items-center gap-1.5 text-xs font-bold mb-0.5">
            <MagnifyingGlass size={14} weight="bold" color="#84CC16" /> What students search for
          </div>
          <div className="text-[11px] text-[#52525B] mb-2">
            Topics typed into the lesson search that led a student to open a lesson — anonymous,
            {" "}{search.total_searches} search click{search.total_searches === 1 ? "" : "s"} total.
          </div>
          <div className="flex flex-col gap-1.5">
            {search.topics.map((t) => (
              <div key={t.q} className="flex flex-wrap items-baseline gap-x-2 text-[12px]" data-testid={`search-topic-${t.q.replace(/\s+/g, "-")}`}>
                <span className="font-bold">“{t.q}”</span>
                <span className="rounded-full bg-[#CCFF00]/40 text-[#3F6212] text-[10px] font-bold px-2 py-0.5">×{t.count}</span>
                {t.lessons.length > 0 && <span className="text-[#71717A]">→ {t.lessons.join(", ")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#C4C8CE] p-6 text-sm text-[#71717A] text-center" data-testid="coach-insights-empty">
          {period === "all"
            ? "No Coach conversations summarized yet. Summaries appear here ~30 minutes after a chat goes quiet."
            : "No Coach conversations in this period — try another range."}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {data.items.map((i) => (
            <div key={i.id} className="rounded-xl border border-[#C4C8CE] bg-white p-3" data-testid="coach-insight-row">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                {i.flagged && <span className="inline-flex items-center gap-1 font-bold text-red-600"><Warning size={12} weight="fill" /> CRISIS FLAG</span>}
                <span className={`rounded-full px-2 py-0.5 font-bold ${COV[i.coverage]?.cls || ""}`}>{COV[i.coverage]?.label || i.coverage}</span>
                {i.redirected && <span className="rounded-full px-2 py-0.5 font-bold bg-[#0A0D14] text-[#CCFF00]" data-testid="insight-redirected-badge" title="Coach steered this user to a better approach than what they asked for">Redirected</span>}
                <span className="font-bold">{i.issue_category}</span>
                <span className="text-[#71717A]">{i.grade || "no grade"}{i.sex ? ` · ${i.sex}` : ""} · {i.audience} · {i.message_count} msgs · {(i.at || "").slice(0, 10)}</span>
              </div>
              <p className="text-sm m-0 mt-1.5">{i.problem_brief}</p>
              {i.lessons_referenced?.length > 0 && (
                <p className="text-xs text-[#52525B] m-0 mt-1">Lessons pointed to: {i.lessons_referenced.join(", ")}</p>
              )}
              {i.gap_notes && (
                <p className="text-xs m-0 mt-1 text-[#B45309]"><b>Gap:</b> {i.gap_notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
